import { ChangeEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PatchCategoryByID, patchCategoryByID } from "../../../api/category.api";
import { State } from "../../../reducer";

function EditCategory() {
  const navigate = useNavigate();
  const category = useSelector((state: State) => state.category);
  const { categoryId } = useParams();
  const categoryIdAsNumber = categoryId ? parseInt(categoryId) : category.category_id;

  const [name, setName] = useState(category.category_name);
  const [categoryFor, setCategoryFor] = useState<string>(category.category_for || "cafe");
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.charAt(0) === " ") {
      setNameError("Name cannot start with a space");
      return;
    }
    setName(v);
    setNameError("");
  };

  const isFormValid = !!name && !!categoryFor && !nameError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    setSubmitting(true);
    try {
      const body: PatchCategoryByID = {
        name,
        categoryFor,
        isAvailable: category.isAvailable || "available",
      };
      await patchCategoryByID(categoryIdAsNumber, body);
      navigate("/menuManagement", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error("Could not save category");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans pb-[120px]">
      <header className="px-5 pt-[24px] pb-[16px] border-b border-ink-100 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-display font-black text-[22px] leading-tight text-ink-1000">Edit category</h1>
      </header>

      <form onSubmit={handleSubmit} className="px-5 pt-[20px] space-y-[20px]">
        <section>
          <label htmlFor="cat-name" className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="cat-name"
            type="text"
            value={name}
            maxLength={50}
            onChange={handleNameChange}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent text-base text-ink-1000 placeholder:text-ink-400 focus:outline-none focus:bg-white focus:border-ink-1000 transition-colors"
          />
          {nameError && <p className="text-xs text-red-500 mt-1.5">{nameError}</p>}
        </section>

        <section>
          <label className="block font-display font-bold text-sm text-ink-1000 mb-2">
            For <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            {(["cafe", "bar", "dessert"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setCategoryFor(v)}
                className={`flex-1 h-[52px] rounded-2xl font-display font-bold text-sm transition border-2 ${
                  categoryFor === v
                    ? "bg-ink-1000 text-white border-ink-1000"
                    : "bg-ink-50 text-ink-700 border-transparent hover:border-ink-200"
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </section>
      </form>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-ink-100 px-5 py-3 flex gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-v2 flex-1 h-[48px] bg-ink-100 text-ink-1000 rounded-2xl"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!isFormValid || submitting}
          onClick={handleSubmit}
          className="btn-v2-primary flex-1 h-[48px] rounded-2xl"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
}

export default EditCategory;
